import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { useGetPricingPlanTickRate } from 'src/features/pricingPlan/api/tickRate/useGetPricingPlanTickRate';
import TickRateForm from 'src/features/pricingPlan/components/form/tickRate/TickRateForm';
import {
    PricingPlanTickRateCreateValues,
    pricingPlanTickRateFormSchema,
    PricingPlanTickRateFormSchemaValues
} from 'src/features/pricingPlan/utils/formSchema';
import { PricingPlanTickRate } from 'src/features/pricingPlan/utils/types';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { cn } from 'src/utils/shadcn';
import { DAY_OF_WEEK } from 'src/features/pricingPlan/utils/types';
import { useParking } from 'src/providers/ParkingProvider';

interface TickRateFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedTickRate?: PricingPlanTickRate;
    children?: React.ReactNode;
    showToast: React.RefObject<boolean>;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: PricingPlanTickRateCreateValues,
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
}

function TickRateFormModal<TData>({
    selectedTickRate,
    className,
                                      showToast,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
}: TickRateFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { parkingId } = useParking();

    const { data: tickRate } = useGetPricingPlanTickRate({
        planId: selectedTickRate?.pricing_plan ?? '',
        parkingId,
        tickRateId: selectedTickRate?.id ?? '',
        config: { enabled: !!selectedTickRate?.pricing_plan && !!selectedTickRate?.id && isModalOpen },
    });

    const onError = useOnError();

    const defaultValues = React.useMemo(() => {
        return {
            day_of_week: tickRate?.day_of_week
                ? [tickRate.day_of_week]
                : [],
            from_time: tickRate?.from_time ?? undefined,
            to_time: tickRate?.to_time ?? undefined,
            rate_with_tax: tickRate?.rate_with_tax ?? undefined,
        }
    }, [tickRate]);

    const form = useForm<PricingPlanTickRateFormSchemaValues>({
        resolver: zodResolver(pricingPlanTickRateFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [tickRate, isModalOpen, form, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(

        (values: PricingPlanTickRateFormSchemaValues) => {

            if (showToast && 'current' in showToast) {
                (showToast as React.RefObject<boolean>).current = false;
            }

            const { day_of_week, ...rest } = values;

            if (!day_of_week || day_of_week.length === 0) return;

            day_of_week.forEach((day: DAY_OF_WEEK) => {
                mutate(
                    {
                        ...rest,
                        day_of_week: day,
                    },
                    () => setIsModalOpen(false),
                    (error) => onError(error, form.setError),
                );
            });
        },
        [mutate, onError, showToast, form, setIsModalOpen],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <TickRateForm form={form} onSubmit={onSubmit} isCreate={!selectedTickRate}/>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedTickRate && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default TickRateFormModal;
